/**
 * IdArrayEditCell - Edit cell for ID array fields (references to other entities)
 *
 * This is the universal edit cell for:
 * - User references (owner, team.AM, team.PM, etc.)
 * - Contact references (primaryContact, contacts, billingContact)
 * - Organization references (contractedBy, affiliated)
 * - Any field that stores array of UUIDs referencing another entity
 *
 * Key behavior:
 * - Auto-opens dropdown on mount (like SelectEditCell)
 * - Search field in dropdown header for filtering
 * - Single select commits immediately and closes
 * - Multiple select stays open for additional selections
 */

import CheckIcon from '@mui/icons-material/Check'
import SearchIcon from '@mui/icons-material/Search'
import {
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  Divider,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium'
import { useVirtualizer } from '@tanstack/react-virtual'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IdArrayConfig } from './types'

interface IdArrayEditCellProps extends GridRenderEditCellParams {
  /** Configuration for this ID array field */
  config: IdArrayConfig
  /** Available items to select from (e.g., all users, all contacts) */
  availableItems: any[]
}

const ITEM_HEIGHT = 40

/** Virtualized list for the dropdown options - only renders visible items */
const VirtualizedOptionsList: React.FC<{
  items: any[]
  idField: string
  selectedIdSet: Set<string>
  referenceType: string
  getLabel: (item: any) => string
  getSecondaryText: (item: any) => string | null
  onItemClick: (id: string) => void
  maxHeight: number
  theme: any
}> = React.memo(({ items, idField, selectedIdSet, referenceType, getLabel, getSecondaryText, onItemClick, maxHeight, theme }) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 8,
  })

  if (items.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography color="text.secondary">No results found</Typography>
      </Box>
    )
  }

  return (
    <div
      ref={parentRef}
      style={{ maxHeight, overflow: 'auto' }}
    >
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map(virtualRow => {
          const item = items[virtualRow.index]
          const itemId = item[idField]
          const isSelected = selectedIdSet.has(itemId)
          const secondary = getSecondaryText(item)

          return (
            <MenuItem
              key={itemId}
              onClick={() => onItemClick(itemId)}
              style={{
                position: 'absolute',
                top: virtualRow.start,
                left: 0,
                width: '100%',
                height: ITEM_HEIGHT,
              }}
              sx={{
                py: 1,
                bgcolor: isSelected ? theme.palette.action.selected : 'transparent',
              }}
            >
              {referenceType === 'user' && item.image_512 && (
                <Avatar src={item.image_512} sx={{ width: 28, height: 28, mr: 1.5 }} />
              )}
              <ListItemText
                primary={getLabel(item)}
                secondary={secondary}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: isSelected ? 500 : 400,
                }}
                secondaryTypographyProps={{ variant: 'caption' }}
                sx={{ textAlign: 'left' }}
              />
            </MenuItem>
          )
        })}
      </div>
    </div>
  )
})

export const IdArrayEditCell: React.FC<IdArrayEditCellProps> = ({ id, field, value, row, config, availableItems }) => {
  const apiRef = useGridApiContext()
  const theme = useTheme()
  const anchorRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [open, setOpen] = useState(false) // Will auto-open after mount
  const [searchText, setSearchText] = useState('')
  const [hasInteracted, setHasInteracted] = useState(false) // Track if user interacted with dropdown

  const { idField, labelField, secondaryLabelField, multiple, referenceType } = config

  // Auto-open after mount when ref is available
  useEffect(() => {
    // Small delay to ensure ref is assigned
    const timer = setTimeout(() => {
      if (anchorRef.current) {
        setOpen(true)
      }
    }, 50) // Slightly longer delay for more reliable opening
    return () => clearTimeout(timer)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 50)
    }
  }, [open])

  // Extract raw IDs from row data (valueGetter may return display string, not raw IDs)
  const currentIds: string[] = useMemo(() => {
    let ids: (string | undefined | null)[] = []

    // For user references (team roles), get raw IDs from row.team[field]
    if (referenceType === 'user' && row?.team?.[field]) {
      const teamValue = row.team[field]
      if (Array.isArray(teamValue)) {
        ids = teamValue
      } else if (teamValue) {
        ids = [teamValue]
      }
    }
    // For owner field
    else if (field === 'owner' && row?.owner) {
      const ownerValue = row.owner
      if (Array.isArray(ownerValue)) {
        ids = ownerValue.map((o: any) => o?.user_uuid || o?.id || o)
      } else if (typeof ownerValue === 'object') {
        ids = [ownerValue?.user_uuid || ownerValue?.id]
      } else {
        ids = [ownerValue]
      }
    }
    // For contact references
    else if (referenceType === 'contact') {
      if (field === 'primaryContact' && row?.primaryContact) {
        const pc = row.primaryContact
        if (typeof pc === 'object' && pc?.contacts_uuid) {
          ids = [pc.contacts_uuid]
        } else if (Array.isArray(pc)) {
          ids = pc.map((c: any) => c?.contacts_uuid || c)
        } else if (pc) {
          ids = [pc]
        }
      } else if (field === 'contacts' && row?.contacts) {
        const contacts = row.contacts
        if (Array.isArray(contacts)) {
          ids = contacts.map((c: any) => c?.contacts_uuid || c)
        } else if (contacts) {
          ids = [contacts]
        }
      }
    }
    // For org references (contractedBy, affiliated)
    else if (referenceType === 'organization') {
      const orgField = row?.[field]
      if (Array.isArray(orgField)) {
        ids = orgField.map((o: any) => o?.org_uuid || o)
      } else if (orgField) {
        ids = [orgField]
      }
    }
    // Fallback: use value prop if it's already an array of IDs
    else if (value) {
      if (Array.isArray(value)) {
        ids = value
      } else {
        ids = [value]
      }
    }

    // Filter out any undefined/null values
    return ids.filter((id): id is string => id != null && id !== '')
  }, [value, row, field, referenceType])

  // Track selected IDs locally for immediate UI feedback
  // Use ref to track the latest value synchronously (for handleClose)
  const selectedIdsRef = useRef<string[]>(currentIds)
  const [selectedIds, _setSelectedIds] = useState<string[]>(currentIds)

  // Custom setter that updates both state and ref synchronously
  // This ensures handleClose always has the latest value, even if called before re-render
  const setSelectedIds = useCallback((valueOrUpdater: string[] | ((prev: string[]) => string[])) => {
    _setSelectedIds(prev => {
      const newValue = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater
      selectedIdsRef.current = newValue // Update ref synchronously
      return newValue
    })
  }, [])

  // O(1) lookup set for selected IDs
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  // Build lookup map for available items
  const itemMap = useMemo(() => {
    const map = new Map<string, any>()
    availableItems?.forEach(item => {
      const itemId = item[idField]
      if (itemId) map.set(itemId, item)
    })
    return map
  }, [availableItems, idField])

  // Get label for an item
  const getLabel = useCallback(
    (item: any) => {
      if (!item) return 'Unknown'
      return item[labelField] || item[secondaryLabelField || ''] || 'Unknown'
    },
    [labelField, secondaryLabelField]
  )

  // Get secondary text for an item
  const getSecondaryText = useCallback(
    (item: any) => {
      if (!secondaryLabelField || !item) return null
      return item[secondaryLabelField]
    },
    [secondaryLabelField]
  )

  // Filter items by search text
  const filteredItems = useMemo(() => {
    if (!searchText.trim()) return availableItems || []
    const lower = searchText.toLowerCase()
    return (availableItems || []).filter(item => {
      const label = getLabel(item)?.toLowerCase() || ''
      const secondary = getSecondaryText(item)?.toLowerCase() || ''
      return label.includes(lower) || secondary.includes(lower)
    })
  }, [availableItems, searchText, getLabel, getSecondaryText])

  // Get selected items for display
  const selectedItems = useMemo(() => {
    return selectedIds.map(itemId => itemMap.get(itemId)).filter(Boolean)
  }, [selectedIds, itemMap])

  // Get display text for current selection (used as fallback)
  const displayText = useMemo(() => {
    if (selectedItems.length === 0) return multiple ? 'Select...' : 'Select one...'
    const names = selectedItems.map(item => getLabel(item)).filter(Boolean)
    return names.join(', ') || 'Select...'
  }, [selectedItems, getLabel, multiple])

  const handleClose = useCallback(() => {
    setOpen(false)
    // Only commit the value if user actually interacted with the dropdown
    // This prevents accidental clearing when popover closes unexpectedly
    if (hasInteracted) {
      // Use ref to get the latest value - fixes stale closure when deselecting quickly
      apiRef.current.setEditCellValue({ id, field, value: selectedIdsRef.current })
      apiRef.current.stopCellEditMode({ id, field })
    } else {
      // Cancel edit mode without committing changes
      apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true })
    }
  }, [apiRef, id, field, hasInteracted])

  const handleItemClick = useCallback(
    (itemId: string) => {
      setHasInteracted(true) // Mark that user has made a selection
      if (multiple) {
        // Toggle selection for multiple
        setSelectedIds(prev => (prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]))
      } else {
        // Single select - commit immediately
        const newIds = [itemId]
        setSelectedIds(newIds)
        apiRef.current.setEditCellValue({ id, field, value: newIds })
        apiRef.current.stopCellEditMode({ id, field })
      }
    },
    [multiple, apiRef, id, field]
  )

  const handleClearSelection = useCallback(() => {
    setHasInteracted(true) // Mark that user intentionally cleared
    setSelectedIds([])
    apiRef.current.setEditCellValue({ id, field, value: [] })
    apiRef.current.stopCellEditMode({ id, field })
  }, [apiRef, id, field])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true })
      }
      // Prevent grid navigation while in dropdown
      event.stopPropagation()
    },
    [apiRef, id, field]
  )

  return (
    <Box ref={anchorRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', px: 1, gap: 0.5 }}>
      {/* Cell display - show avatars for users, chips/text for others */}
      {referenceType === 'user' && selectedItems.length > 0 ? (
        <AvatarGroup
          max={4}
          sx={{
            cursor: 'pointer',
            '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' },
          }}
          onClick={() => setOpen(true)}
        >
          {selectedItems.map(item => (
            <Avatar
              key={item[idField]}
              src={item.image_512 || item.image_48}
              alt={getLabel(item)}
              sx={{ width: 24, height: 24 }}
            >
              {getLabel(item)?.[0]}
            </Avatar>
          ))}
        </AvatarGroup>
      ) : selectedItems.length > 0 ? (
        <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setOpen(true)}>
          {selectedItems.slice(0, 2).map(item => (
            <Chip
              key={item[idField]}
              label={getLabel(item)}
              size="small"
              sx={{ maxWidth: 100, height: 20, fontSize: '0.7rem' }}
            />
          ))}
          {selectedItems.length > 2 && (
            <Typography variant="caption" color="text.secondary">
              +{selectedItems.length - 2}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ cursor: 'pointer', flex: 1 }}
          onClick={() => setOpen(true)}
        >
          {displayText}
        </Typography>
      )}

      {/* Dropdown popover */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.paper,
              minWidth: 280,
              maxHeight: '100dvh',
              boxShadow: theme.shadows[8],
            },
          },
        }}
      >
        <Box onKeyDown={handleKeyDown}>
          {/* Search field in dropdown header */}
          <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <TextField
              inputRef={searchInputRef}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search..."
              size="small"
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.background.default,
                },
              }}
            />
          </Box>

          {/* Selected items section */}
          {selectedItems.length > 0 && (
            <>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  bgcolor: theme.palette.action.selected,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Selected ({selectedItems.length})
                </Typography>
                {/* Clear selection button for single-select */}
                {!multiple && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={handleClearSelection}
                  >
                    Clear
                  </Typography>
                )}
              </Box>
              <MenuList sx={{ py: 0 }}>
                {selectedItems.map(item => {
                  const itemId = item[idField]
                  const secondary = getSecondaryText(item)
                  return (
                    <MenuItem
                      key={`selected-${itemId}`}
                      onClick={() => handleItemClick(itemId)}
                      sx={{ py: 0.75, bgcolor: theme.palette.action.hover }}
                    >
                      <CheckIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                      {referenceType === 'user' && item.image_512 && (
                        <Avatar src={item.image_512} sx={{ width: 24, height: 24, mr: 1 }} />
                      )}
                      <ListItemText
                        primary={getLabel(item)}
                        secondary={secondary}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                        sx={{ textAlign: 'left' }}
                      />
                    </MenuItem>
                  )
                })}
              </MenuList>
              <Divider />
            </>
          )}

          {/* All options list (virtualized) */}
          <VirtualizedOptionsList
            items={filteredItems}
            idField={idField}
            selectedIdSet={selectedIdSet}
            referenceType={referenceType}
            getLabel={getLabel}
            getSecondaryText={getSecondaryText}
            onItemClick={handleItemClick}
            maxHeight={selectedItems.length > 0 ? 220 : 300}
            theme={theme}
          />
        </Box>
      </Popover>
    </Box>
  )
}

export default IdArrayEditCell
